import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Clock, ChevronRight, Lock, Unlock, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Design tokens from v2.1 spec
const tokens = {
  bg: '#08090b',
  surface: '#0f1114',
  surface2: '#161a1f',
  border: 'rgba(255,255,255,0.06)',
  text: '#e8e2d6',
  muted: 'rgba(237,232,223,0.68)',
  dim: 'rgba(237,232,223,0.38)',
  ground: '#4CAF7A',
  lock: '#C9A84C',
  observe: '#4A90D9',
};

export const WhatNext = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sessionCount, setSessionCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get latest completion data
      const [completionsRes, statsRes, baselineRes] = await Promise.all([
        axios.get(`${API}/completions`, { withCredentials: true }),
        axios.get(`${API}/completions/stats`, { withCredentials: true }),
        axios.get(`${API}/baseline`, { withCredentials: true })
      ]);

      const completions = completionsRes.data || [];
      const totalSessions = completions.length;
      setSessionCount(totalSessions);

      // Get latest baseline
      const baselines = baselineRes.data || [];
      const latestBaseline = baselines.length > 0 ? baselines[0].exhale_duration : 15;
      const baselineScore = Math.min(100, Math.round((latestBaseline / 45) * 100));

      // Calculate streak
      const currentStreak = statsRes.data?.current_streak || 1;
      const streakMultiplier = currentStreak >= 21 ? 1.4 : 
                              currentStreak >= 14 ? 1.3 : 
                              currentStreak >= 7 ? 1.2 : 
                              currentStreak >= 3 ? 1.1 : 1.0;

      // Calculate today's delta using v2.1 algorithm
      const baseRate = 3.0;
      const baselineMultiplier = baselineScore / 70;
      const completionMultiplier = 1.0; // 100% completion
      const hrvMultiplier = 1.1; // Default medium
      const todayDelta = baseRate * baselineMultiplier * completionMultiplier * hrvMultiplier * streakMultiplier;

      // Calculate tomorrow's projected delta
      const tomorrowStreakMultiplier = currentStreak >= 20 ? 1.4 : 
                                       currentStreak >= 13 ? 1.3 : 
                                       currentStreak >= 6 ? 1.2 : 
                                       currentStreak >= 2 ? 1.1 : 1.0;
      const synapticBonus = 1.2; // 18-24 hour window bonus
      const tomorrowDelta = baseRate * baselineMultiplier * completionMultiplier * hrvMultiplier * tomorrowStreakMultiplier * synapticBonus;

      // Lifetime NP (estimate)
      const lifetimeNP = Math.round(totalSessions * todayDelta * 10) + 1000;

      setSessionData({
        sessionNumber: totalSessions,
        todayDelta: todayDelta.toFixed(1),
        tomorrowDelta: tomorrowDelta.toFixed(1),
        lifetimeNP,
        baselineScore,
        streakMultiplier: streakMultiplier.toFixed(1),
        currentStreak,
        hrvLevel: 'High',
      });

      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set default data on error
      setSessionData({
        sessionNumber: 1,
        todayDelta: '4.7',
        tomorrowDelta: '6.1',
        lifetimeNP: 1847,
        baselineScore: 72,
        streakMultiplier: '1.3',
        currentStreak: 14,
        hrvLevel: 'High',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetReminder = () => {
    // For now, just show a message. In production, this would trigger native notifications
    if ('Notification' in window && Notification.permission === 'granted') {
      alert('Reminder set for tomorrow morning!');
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          alert('Notifications enabled! Reminder set for tomorrow.');
        }
      });
    } else {
      alert('Reminder noted! Enable notifications in your device settings for push reminders.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: tokens.bg }}>
        <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: tokens.lock, borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  const showScenarioUnlock = sessionCount >= 10;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: tokens.bg }}>
      <div className="max-w-lg mx-auto px-4 py-8">
        
        {/* Completion Confirmation */}
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${tokens.ground}15`, border: `2px solid ${tokens.ground}` }}
          >
            <Check className="w-8 h-8" style={{ color: tokens.ground }} />
          </div>
          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: tokens.muted, fontFamily: 'DM Mono, monospace' }}>
            Session {sessionData.sessionNumber} complete
          </p>
          <h1 className="text-3xl font-serif mb-2" style={{ color: tokens.text, fontFamily: 'DM Serif Display, serif' }}>
            Protocol done.
          </h1>
          <p style={{ color: tokens.muted }}>
            15 minutes. 6 steps. All complete.
          </p>
        </div>

        {/* Today's Gain Card */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ 
            backgroundColor: tokens.surface,
            background: `linear-gradient(135deg, ${tokens.ground}08 0%, ${tokens.ground}03 100%)`,
            border: `1px solid ${tokens.ground}33`
          }}
          data-testid="today-gain-card"
        >
          <h2 
            className="text-5xl font-serif text-center mb-2"
            style={{ color: tokens.ground, fontFamily: 'DM Serif Display, serif' }}
          >
            +{sessionData.todayDelta}%
          </h2>
          <p className="text-center text-sm mb-4" style={{ color: tokens.muted }}>
            Neuroplasticity added today
          </p>
          <p className="text-sm text-center" style={{ color: tokens.dim }}>
            Your baseline was {sessionData.baselineScore}. HRV coherence peaked during Yoga Nidra. 
            Streak multiplier active. Total lifetime score: <strong style={{ color: tokens.text }}>{sessionData.lifetimeNP.toLocaleString()} NP</strong>.
          </p>
        </div>

        {/* Tomorrow's Projection */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.border}` }}
          data-testid="tomorrow-projection"
        >
          <h3 className="text-lg font-serif mb-1" style={{ color: tokens.text, fontFamily: 'DM Serif Display, serif' }}>
            Tomorrow's session will add
          </h3>
          <p className="text-3xl font-serif mb-4" style={{ color: tokens.lock, fontFamily: 'DM Serif Display, serif' }}>
            +{sessionData.tomorrowDelta}% neuroplasticity
          </p>
          <p className="text-sm mb-4" style={{ color: tokens.muted }}>
            Your synaptic consolidation window peaks 18-24 hours post-session. 
            Practising tomorrow captures the maximum learning transfer window. 
            Missing tomorrow reduces the gain to ~2.3%.
          </p>

          {/* State unlock preview */}
          <div 
            className="flex items-start gap-3 p-3 rounded-lg"
            style={{ backgroundColor: `${tokens.lock}10`, border: `1px solid ${tokens.lock}25` }}
          >
            <Unlock className="w-5 h-5 mt-0.5" style={{ color: tokens.lock }} />
            <div>
              <p className="text-sm font-medium" style={{ color: tokens.text }}>
                Integrate state unlocks after 3 more sessions
              </p>
              <p className="text-xs" style={{ color: tokens.dim }}>
                Your Integrate score is at 5.5 - it activates above 6.0.
              </p>
            </div>
          </div>
        </div>

        {/* Alarm CTA */}
        <button
          onClick={handleSetReminder}
          className="w-full py-4 px-6 rounded-lg font-medium flex items-center justify-center gap-2 mb-4 transition-all hover:opacity-90"
          style={{ 
            backgroundColor: tokens.lock, 
            color: '#1a1200',
            fontFamily: 'DM Mono, monospace',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em'
          }}
          data-testid="set-reminder-btn"
        >
          <Clock className="w-4 h-4" />
          Set reminder for tomorrow
        </button>

        <button
          onClick={() => navigate('/progress')}
          className="w-full text-center text-sm py-2"
          style={{ color: tokens.muted }}
          data-testid="not-now-btn"
        >
          Not now
        </button>

        {/* Scenario Lab Unlock - shows at session 10+ */}
        {showScenarioUnlock && (
          <div 
            className="mt-8 rounded-2xl p-6"
            style={{ 
              backgroundColor: tokens.surface,
              border: `1px solid #C07FD933`
            }}
            data-testid="scenario-unlock-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" style={{ color: '#C07FD9' }} />
              <span 
                className="text-xs uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ 
                  backgroundColor: '#C07FD920',
                  color: '#C07FD9',
                  fontFamily: 'DM Mono, monospace'
                }}
              >
                Unlocked
              </span>
            </div>
            <h3 className="text-xl font-serif mb-2" style={{ color: tokens.text, fontFamily: 'DM Serif Display, serif' }}>
              Scenario Lab
            </h3>
            <p className="text-sm mb-4" style={{ color: tokens.muted }}>
              You're ready for this. {sessionCount} sessions complete. Your substrate is built. 
              Scenario Lab is now open - train state sequencing for the moments that matter.
            </p>
            <button
              onClick={() => navigate('/scenarios')}
              className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
              style={{ color: '#C07FD9' }}
              data-testid="enter-scenario-lab"
            >
              Enter Scenario Lab
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default WhatNext;
